<template>
  <modal-view
    :title="`Transfer #${transfer?.local_transfer_id || transfer?.id || ''}`"
    :custom-max-width="'1000px'"
    :custom-content-height="'85vh'"
    custom-class="catalog-transfer-modal"
    custom-class-content="catalog-transfer-modal__content"
    @close-modal="$emit('close')"
  >
    <div class="transfer-details-modern flex h-full flex-col">
      <!-- Header Info -->
      <div
        class="transfer-modal-hero border-b border-gray-200 px-6 py-4 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1 space-y-3">
            <!-- Main Info Row -->
            <div class="flex items-start gap-6">
              <!-- From/To Column -->
              <div class="text-sm space-y-1.5 min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >From:</span
                  >
                  <span class="text-gray-900 dark:text-white truncate">{{
                    storeName(transfer?.from_store_id)
                  }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >To:</span
                  >
                  <span class="text-gray-900 dark:text-white truncate">{{
                    storeName(transfer?.to_store_id)
                  }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >Status:</span
                  >
                  <span
                    :class="[
                      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                      getStatusClass(transfer?.status),
                    ]"
                  >
                    {{ transfer?.status }}
                  </span>
                </div>
              </div>

              <!-- Timeline Column -->
              <div class="text-sm space-y-1.5 min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <i class="fas fa-calendar-plus text-gray-400 w-4"></i>
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >Created:</span
                  >
                  <span class="text-gray-900 dark:text-white text-xs">{{
                    formatDateTime(transfer?.created_at)
                  }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <i class="fas fa-clock text-gray-400 w-4"></i>
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >Updated:</span
                  >
                  <span class="text-gray-900 dark:text-white text-xs">{{
                    formatDateTime(transfer?.updated_at)
                  }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <i class="fas fa-user text-gray-400 w-4"></i>
                  <span class="font-semibold text-gray-700 dark:text-gray-300"
                    >By:</span
                  >
                  <span class="text-gray-900 dark:text-white text-xs">{{
                    transfer?.recorded_by?.names || "N/A"
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Note Section -->
            <div
              v-if="transfer?.note"
              class="pt-2 border-t border-gray-200 dark:border-gray-700"
            >
              <div class="flex items-start gap-2">
                <i class="fas fa-sticky-note text-amber-500 mt-0.5"></i>
                <div class="flex-1 min-w-0">
                  <span
                    class="font-semibold text-gray-700 dark:text-gray-300 text-sm"
                    >Note:</span
                  >
                  <p
                    class="text-sm text-gray-600 dark:text-gray-400 mt-0.5 whitespace-pre-wrap"
                  >
                    {{ transfer?.note }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Items List -->
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <div v-if="loading" class="flex items-center justify-center py-12">
          <div class="text-center">
            <div
              class="inline-flex h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-r-transparent"
            ></div>
            <p class="mt-3 text-sm text-gray-600 dark:text-gray-400">
              Loading transfer details...
            </p>
          </div>
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="item in items"
            :key="item.id"
            class="transfer-line-card rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900 overflow-hidden"
          >
            <!-- Header with quantity -->
            <div
              class="bg-gray-50 dark:bg-gray-800/50 px-4 py-2.5 flex items-center justify-between border-b border-gray-200 dark:border-gray-700"
            >
              <span
                class="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200"
              >
                <i class="fas fa-check-circle mr-1.5"></i>
                Transferred
              </span>
              <span
                class="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 font-semibold text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-sm"
              >
                <i class="fas fa-exchange-alt mr-2"></i>
                {{ quantityLabel(item) }}
              </span>
            </div>

            <div
              v-if="item.unit_conversion_label"
              class="border-b border-blue-200 bg-blue-50 px-4 py-2 text-xs font-medium text-blue-800 dark:border-blue-900 dark:bg-blue-900/20 dark:text-blue-200"
            >
              <i class="fas fa-scale-balanced mr-1.5"></i>
              {{ item.unit_conversion_label }}
              <span
                v-if="requiresExplicitConversion(item)"
                class="ml-2 font-semibold text-amber-700 dark:text-amber-300"
              >
                Mapping conversion should be reviewed.
              </span>
            </div>

            <!-- 50/50 Product Display -->
            <div class="transfer-product-grid grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
              <!-- LEFT: Source Product (Sent From) -->
              <div class="transfer-product-pane transfer-product-pane--out p-4 bg-red-50/30 dark:bg-red-900/5">
                <div class="flex items-center gap-2 mb-2">
                  <i
                    class="fas fa-minus-circle text-red-600 dark:text-red-400"
                  ></i>
                  <span
                    class="text-xs font-bold uppercase text-gray-700 dark:text-gray-300"
                  >
                    Sent From
                  </span>
                </div>
                <h4
                  class="text-sm font-semibold text-gray-900 dark:text-white mb-2"
                >
                  {{
                    item.from_product?.name ||
                    `Product #${item.from_product_id}`
                  }}
                </h4>
                <div class="space-y-1.5 text-xs">
                  <div
                    class="flex items-center gap-2 text-gray-600 dark:text-gray-400"
                  >
                    <span class="font-medium w-16">SKU:</span>
                    <span class="font-mono">{{
                      item.from_product?.item_sku ||
                      item.from_product?.sku ||
                      "N/A"
                    }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span
                      class="font-medium w-16 text-gray-600 dark:text-gray-400"
                      >Store:</span
                    >
                    <span class="font-medium text-red-700 dark:text-red-300">
                      {{ storeName(transfer?.from_store_id) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- RIGHT: Target Product (Received By) -->
              <div class="transfer-product-pane transfer-product-pane--in p-4 bg-green-50/30 dark:bg-green-900/5">
                <div class="flex items-center gap-2 mb-2">
                  <i
                    class="fas fa-plus-circle text-green-600 dark:text-green-400"
                  ></i>
                  <span
                    class="text-xs font-bold uppercase text-gray-700 dark:text-gray-300"
                  >
                    Received By
                  </span>
                </div>
                <h4
                  class="text-sm font-semibold text-gray-900 dark:text-white mb-2"
                >
                  {{
                    item.to_product?.name || `Product #${item.to_product_id}`
                  }}
                </h4>
                <div class="space-y-1.5 text-xs">
                  <div
                    class="flex items-center gap-2 text-gray-600 dark:text-gray-400"
                  >
                    <span class="font-medium w-16">SKU:</span>
                    <span class="font-mono">{{
                      item.to_product?.item_sku || item.to_product?.sku || "N/A"
                    }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span
                      class="font-medium w-16 text-gray-600 dark:text-gray-400"
                      >Store:</span
                    >
                    <span
                      class="font-medium text-green-700 dark:text-green-300"
                    >
                      {{ storeName(transfer?.to_store_id) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="items.length === 0 && !loading" class="text-center py-12">
            <i
              class="fas fa-box-open text-4xl text-gray-300 dark:text-gray-600 mb-4"
            ></i>
            <p class="text-gray-600 dark:text-gray-400">
              No items in this transfer
            </p>
          </div>
        </div>
      </div>
    </div>
  </modal-view>
</template>

<script>
import { ref, watch } from "vue";
import ModalView from "@/components/modals/ModalView.vue";
import http from "@/helper/http";

export default {
  name: "TransferDetailsModal",
  components: { ModalView },
  props: {
    isOpen: { type: Boolean, default: false },
    groupId: { type: [String, Number], required: true },
    transfer: { type: Object, default: null },
    storeName: { type: Function, required: true },
  },
  emits: ["close"],
  setup(props) {
    const items = ref([]);
    const loading = ref(false);

    const getStatusClass = (status) => {
      const classes = {
        PENDING:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
        COMPLETED:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        FAILED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
      };
      return classes[status] || classes.PENDING;
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        // Show relative time for recent dates
        if (diffMins < 1) return "Just now";
        if (diffMins < 60)
          return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
        if (diffHours < 24)
          return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
        if (diffDays < 7)
          return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

        // Show full date for older dates using global utility
        return this.$formatDateTime(dateString);
      } catch {
        return String(dateString);
      }
    };

    const quantityLabel = (item) => {
      const unit =
        item?.quantity_unit_label ||
        item?.source_quantity_snapshot?.quantity_unit_label ||
        item?.quantity_unit ||
        "U";
      return `${item?.quantity ?? 0} ${unit}`;
    };

    const requiresExplicitConversion = (item) => {
      const sourceBase = item?.source_quantity_snapshot?.base_quantity_unit;
      const targetBase = item?.target_quantity_snapshot?.base_quantity_unit;
      return !!sourceBase && !!targetBase && sourceBase !== targetBase;
    };

    const fetchDetails = async () => {
      if (!props.transfer?.id || !props.groupId) return;

      loading.value = true;
      try {
        console.log("🔄 Fetching transfer details...");
        const { data } = await http.get(
          `/v2/groups/${props.groupId}/transfers/${props.transfer.id}`
        );
        items.value = Array.isArray(data?.items) ? data.items : [];
        console.log("✅ Received transfer items:", items.value);
      } catch (e) {
        console.error("❌ Failed to fetch transfer details", e);
      } finally {
        loading.value = false;
      }
    };

    watch(
      () => props.isOpen,
      async (val) => {
        if (val && props.transfer?.id) {
          await fetchDetails();
        } else {
          items.value = [];
        }
      },
      { immediate: true }
    );

    return {
      items,
      loading,
      getStatusClass,
      formatDateTime,
      quantityLabel,
      requiresExplicitConversion,
    };
  },
};
</script>

<style scoped>
:deep(.catalog-transfer-modal) {
  background:
    radial-gradient(circle at 92% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 13%, transparent), transparent 30%),
    var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 28px;
  box-shadow: 0 34px 100px rgba(15, 23, 42, 0.32);
  overflow: hidden;
}

:deep(.catalog-transfer-modal .modal-title) {
  color: var(--hs-text, #0f172a);
  font-size: 1.4rem;
  font-weight: 950;
}

:deep(.catalog-transfer-modal .close-btn) {
  align-items: center;
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
  border-radius: 14px;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  transition: background 0.2s ease, transform 0.2s ease;
  width: 40px;
}

:deep(.catalog-transfer-modal .close-btn:hover) {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent);
  transform: translateY(-1px);
}

.transfer-modal-hero {
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent), transparent),
    color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 74%, var(--hs-surface, #ffffff));
}

.transfer-line-card {
  border-radius: 20px;
  border-color: var(--hs-border, rgba(148, 163, 184, 0.22));
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.transfer-line-card:hover {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 26%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.11);
  transform: translateY(-2px);
}

.transfer-product-pane {
  min-height: 132px;
}

@media (max-width: 760px) {
  .transfer-product-grid {
    grid-template-columns: 1fr;
  }
}
</style>
